import React, { useState } from 'react';
import { Check, CheckCircle2, ChevronRight, Loader2, Lock, ShieldCheck } from 'lucide-react';
import { AckSectionKey, Chip, Flow, FlowUnit, SectionKey } from './shared';
import { parseServerTime } from '../../../../utils/serverTime';
import { formatDateToDdmmyyyy } from '../../../../utils/format';

// Before and after acknowledging: how to report a mistake.
const WrongDetailsNote: React.FC<{ className?: string }> = ({ className = '' }) => (
    <p className={`px-3.5 py-2.5 rounded-xl bg-amber-50 ring-1 ring-amber-200 text-[13px] text-amber-950 ${className}`} role="note">
        If any details are wrong, take a screenshot and send it in this WhatsApp chat with the correct values.
    </p>
);

// Acknowledgment: once our team verifies it, the investor confirms KYC, bank and every
// unit's nominee one by one (SectionAck), each from its own WhatsApp link (ank_verify),
// pinned to the exact verified version. The profile is complete when all are acknowledged.

export const ACK_LABELS: Record<AckSectionKey, string> = { kyc: 'KYC details', bank: 'Bank details', nominees: 'Unit nominees' };

const fmtDate = (v?: string | null) => {
    if (!v) return '';
    const d = parseServerTime(v);
    return isNaN(d.getTime()) ? '' : formatDateToDdmmyyyy(d);
};

const ACK_CHIP: Record<string, { label: string; cls: string }> = {
    locked: { label: 'After verification', cls: 'bg-slate-100 text-slate-600 ring-1 ring-slate-300' },
    ready: { label: 'Please confirm', cls: 'bg-[#1B4332] text-white' },
    sent: { label: 'Please confirm', cls: 'bg-[#1B4332] text-white' },
    done: { label: 'Acknowledged', cls: 'bg-emerald-100 text-emerald-900' },
    'n/a': { label: 'Not needed', cls: 'bg-slate-50 text-slate-500' },
};

export const unitAckLabel = (u: FlowUnit) => `Unit ${u.unit_index + 1} nominee`;

/** Shown at the bottom of a section once it is verified — or, with `unit`, for one
 *  unit's nominee (several can show at once, so the button stays inline on phones).
 *  `compact`: inside that unit's own card, which already shows its order and nominee. */
export const SectionAck: React.FC<{
    flow: Flow; section: AckSectionKey; busy: boolean; unit?: FlowUnit; compact?: boolean;
    onAcknowledge: (section: AckSectionKey, version: string, unitId?: string) => void;
}> = ({ flow, section, busy, unit, compact, onAcknowledge }) => {
    const [checked, setChecked] = useState(false);
    const a = unit ? unit.ack : flow.acks?.[section];
    if (!a || a.status === 'locked' || a.status === 'n/a') return null;
    const label = unit ? unitAckLabel(unit) : ACK_LABELS[section];
    const short = unit ? `Unit ${unit.unit_index + 1}` : label.split(' ')[0];
    const ack = () => onAcknowledge(section, a.version, unit?.unit_id);
    const nominee = unit?.allocations[0]?.nominee;

    if (a.status === 'done') {
        if (compact) return (
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 text-emerald-950 text-[12px]">
                <CheckCircle2 size={16} className="shrink-0 text-emerald-700" />
                <span><b>Acknowledgement completed</b>{a.acknowledged_at ? ` · ${fmtDate(a.acknowledged_at)}` : ''}</span>
            </div>
        );
        return (
            // Small card: done, and when. Several units can show at once, so each names its unit.
            <div className="mt-6 flex flex-col items-center">
                <div className="inline-flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 text-emerald-950">
                    <CheckCircle2 size={18} className="shrink-0 text-emerald-700" />
                    <span className="leading-tight">
                        <b className="block text-[13px]">Acknowledgement completed</b>
                        {(unit || a.acknowledged_at) && <span className="block text-[11px] text-emerald-800/80 mt-0.5">{[unit && short, fmtDate(a.acknowledged_at)].filter(Boolean).join(' · ')}</span>}
                    </span>
                </div>
                <WrongDetailsNote className="mt-3 w-full max-w-xl text-center" />
            </div>
        );
    }

    return (
        <>
        <div className={`${compact ? '' : 'mt-6 '}rounded-2xl border-2 border-[#1B4332] bg-[#F4FAF6] ${compact ? 'p-3.5' : 'p-4 sm:p-5'}`}>
            <div className="flex items-start gap-3">
                <ShieldCheck size={24} className="text-[#1B4332] shrink-0" />
                <div className="text-sm"><b className="text-base">Our team verified your {label.toLowerCase()}</b>
                    <p className="text-slate-600 mt-0.5">Please confirm from your side.</p></div>
            </div>
            {unit && !compact && (
                <div className="mt-3 rounded-xl bg-white p-3.5 border border-slate-200 text-sm">
                    <div className="flex justify-between gap-3 py-1"><span className="text-slate-500">Order</span><b className="font-mono">{unit.order_id || '—'}</b></div>
                    <div className="flex justify-between gap-3 py-1"><span className="text-slate-500">Nominee</span><b className="text-right">{nominee ? `${nominee.nominee_full_name} · ${nominee.relation}` : '—'}</b></div>
                </div>
            )}
            <label className={`mt-4 flex gap-3 items-start p-3.5 rounded-xl border-2 bg-white cursor-pointer ${checked ? 'border-[#1B4332]' : 'border-slate-300'}`}>
                <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} className="w-6 h-6 mt-0.5 accent-[#1B4332] shrink-0" />
                <span className="text-sm"><b>I confirm my {label.toLowerCase()} {unit ? 'is' : 'are'} correct.</b></span>
            </label>
            <div className={`mt-4 flex flex-col sm:flex-row gap-3 sm:justify-end ${unit ? '' : 'max-lg:hidden'}`}>
                <button type="button" onClick={ack} disabled={!checked || busy}
                    className="min-h-[48px] px-6 rounded-xl bg-[#1B4332] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40">
                    {busy && <Loader2 size={16} className="animate-spin" />}Acknowledge {short}
                </button>
            </div>
            {/* Shown on every screen (on phones the button itself is in the bottom bar). */}
            <WrongDetailsNote className="mt-4" />
        </div>
            {!unit && <>
            {/* Mobile: the button sits in the bottom bar, where Save / Send correction would be. */}
            <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]">
                <button type="button" onClick={ack} disabled={!checked || busy}
                    className="w-full min-h-[52px] rounded-2xl bg-[#1B4332] text-white font-bold inline-flex items-center justify-center gap-2 disabled:bg-[#1B4332]/40">
                    {busy && <Loader2 size={16} className="animate-spin" />}Acknowledge {short}
                </button>
                <p className="mt-1.5 text-center text-xs text-slate-500">{checked ? 'Ready to acknowledge' : 'Tick the box above to confirm'}</p>
            </div>
            <div className="lg:hidden h-20" />
            </>}
        </>
    );
};

/** Section 4: where KYC, bank and every unit's acknowledgment stands; each row opens
 *  the section to confirm it in. */
const AckSection: React.FC<{ flow: Flow; onGo: (s: SectionKey) => void }> = ({ flow, onGo }) => {
    const rows: { key: string; go: SectionKey; label: string; a: { status: string; acknowledged_at?: string | null }; verify: string }[] = [
        ...(['kyc', 'bank'] as AckSectionKey[]).map(k => ({ key: k, go: k as SectionKey, label: ACK_LABELS[k], a: flow.acks?.[k] || { status: 'locked' }, verify: flow.sections[k] })),
        ...flow.units.map(u => ({ key: u.unit_id, go: 'nominees' as SectionKey, label: unitAckLabel(u), a: u.ack || { status: 'locked' }, verify: u.status })),
    ];
    const required = rows.filter(r => r.a.status !== 'n/a');
    const done = required.filter(r => r.a.status === 'done').length;

    return (
        <div className="mt-6">
            {flow.sections.ack === 'done' ? (
                <div className="flex flex-col items-center text-center px-5 py-7 rounded-3xl bg-emerald-50">
                    <span className="w-16 h-16 rounded-full bg-[#1B4332] text-white grid place-items-center"><Check size={32} strokeWidth={3} /></span>
                    <b className="mt-4 text-xl font-extrabold text-[#14231B]">Profile complete</b>
                    {flow.investor.acknowledged_at && <span className="mt-1 text-sm font-semibold text-emerald-900">Completed {fmtDate(flow.investor.acknowledged_at)}</span>}
                    <p className="mt-2 text-[13px] text-slate-600 max-w-xs">If anything changes later, we'll ask you to confirm that section again.</p>
                </div>
            ) : (
                <div className="flex gap-3 items-center p-4 rounded-2xl bg-slate-50 text-slate-700">
                    <Lock size={22} className="shrink-0" />
                    <p className="text-sm"><b>{done} of {required.length} acknowledged.</b> Once our team verifies your KYC, your bank details and each unit's nominee, we'll send you a link to confirm each one.</p>
                </div>
            )}
            <div className="mt-5 rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
                {rows.map(({ key, go, label, a, verify }, i) => {
                    const chip = ACK_CHIP[a.status] || ACK_CHIP.locked;
                    const actionable = a.status === 'ready' || a.status === 'sent';
                    const ok = a.status === 'done';
                    const sub = ok ? `Acknowledged ${fmtDate(a.acknowledged_at)}` : a.status === 'n/a' ? 'No units yet' : null;
                    return (
                        <button key={key} type="button" onClick={() => onGo(go)}
                            className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-slate-50 transition">
                            <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 text-[15px] font-extrabold ${ok ? 'bg-emerald-100 text-[#1B4332]' : 'bg-slate-100 text-slate-500'}`}>
                                {ok ? <Check size={18} strokeWidth={3} /> : i + 1}
                            </span>
                            <span className="flex-1 min-w-0">
                                <b className="block text-[15px] text-[#14231B]">{label}</b>
                                <span className="block text-xs text-slate-500 truncate">{sub ?? <>Verification: <Chip status={verify} /></>}</span>
                            </span>
                            {actionable
                                ? <span className="h-8 px-3 rounded-lg bg-[#1B4332] text-white text-xs font-bold inline-flex items-center shrink-0">Review & confirm</span>
                                : !ok && <span className={`h-6 px-2.5 inline-flex items-center rounded-full text-[11px] font-bold shrink-0 ${chip.cls}`}>{chip.label}</span>}
                            <ChevronRight size={18} className="text-slate-400 shrink-0" />
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default AckSection;
